import type { SupportedAsset, SupportedAssetToken } from '@/lib/predict-api';
import type { ChainInfo } from '@/lib/dex-api';
import { chainList } from '@/app/const/chain';

/** 应用内 Solana chainId（与 chainList 一致） */
export const SOLANA_CHAIN_ID = 501;
/** Polymarket Bridge API 返回的 Solana 数值 chainId，归一化后为 SOLANA_CHAIN_ID */
export const POLYMARKET_BRIDGE_SOLANA_NUMERIC_ID = 1151111081099710;

/** 预测提现：这些网络不展示「填入钱包地址」快捷入口 */
export const PREDICT_WITHDRAW_QUICK_FILL_HIDDEN_CHAIN_IDS = new Set<number>([8253038, 728126428]);

/** 应用内 chainId（字符串或数字）转为 Bridge API 要求的 toChainId/fromChainId 字符串 */
export function toPolymarketBridgeChainId(chainId: string | number): string {
  const n = Number(chainId);
  if (n === SOLANA_CHAIN_ID) return String(POLYMARKET_BRIDGE_SOLANA_NUMERIC_ID);
  return String(chainId);
}
export const BITCOIN_CHAIN_ID = 8253038;
export const TRON_CHAIN_ID = 728126428;

/** pd 网络Logo映射 */
export const pdChainLogos: Record<number, { chainName: string; logo: string }> = {
  1: { chainName: 'Ethereum', logo: 'https://sdk-cdn.fun.xyz/images/ethereum.svg' },
  10: { chainName: 'Optimism', logo: 'https://sdk-cdn.fun.xyz/images/optimism.svg' },
  56: { chainName: 'BNB Smart Chain', logo: 'https://sdk-cdn.fun.xyz/images/bsc.svg' },
  137: { chainName: 'Polygon', logo: 'https://sdk-cdn.fun.xyz/images/polygon.svg' },
  143: { chainName: 'Monad', logo: 'https://sdk-cdn.fun.xyz/images/monad.svg' },
  999: { chainName: 'HyperEVM', logo: 'https://sdk-cdn.fun.xyz/images/hyperevm.svg' },
  2741: { chainName: 'Abstract', logo: 'https://sdk-cdn.fun.xyz/images/abstract.svg' },
  8453: { chainName: 'Base', logo: 'https://sdk-cdn.fun.xyz/images/base.svg' },
  42161: { chainName: 'Arbitrum', logo: 'https://sdk-cdn.fun.xyz/images/arbitrum.svg' },
  5064014: { chainName: 'Ethereal', logo: 'https://sdk-cdn.fun.xyz/images/ethereal.svg' },
  8253038: { chainName: 'Bitcoin', logo: 'https://sdk-cdn.fun.xyz/images/bitcoin.svg' },
  728126428: { chainName: 'Tron', logo: 'https://sdk-cdn.fun.xyz/images/tron.svg' },
  1151111081099710: { chainName: 'Solana', logo: 'https://sdk-cdn.fun.xyz/images/solana.svg' },
  501: { chainName: 'Solana', logo: 'https://sdk-cdn.fun.xyz/images/solana.svg' },
};

// polymarket token logo 映射，一共24个token
export const pdTokenLogos: Record<string, string> = {
  USDC: 'https://sdk-cdn.fun.xyz/images/usdc.svg',
  'USDC.e': 'https://sdk-cdn.fun.xyz/images/usdc.svg',
  ARB: 'https://sdk-cdn.fun.xyz/images/arb.svg',
  BNB: 'https://sdk-cdn.fun.xyz/images/bsc.svg',
  BTC: 'https://sdk-cdn.fun.xyz/images/btc.svg',
  BTCB: 'https://sdk-cdn.fun.xyz/images/btc.svg',
  BUSD: 'https://sdk-cdn.fun.xyz/images/busd.svg',
  CBBTC: 'https://sdk-cdn.fun.xyz/images/cbbtc.svg',
  DAI: 'https://sdk-cdn.fun.xyz/images/dai.svg',
  ETH: 'https://sdk-cdn.fun.xyz/images/eth.svg',
  HYPE: 'https://sdk-cdn.fun.xyz/images/hype.svg',
  MATIC: 'https://sdk-cdn.fun.xyz/images/matic.svg',
  MON: 'https://sdk-cdn.fun.xyz/images/monad.svg',
  POL: 'https://sdk-cdn.fun.xyz/images/matic.svg',
  SOL: 'https://sdk-cdn.fun.xyz/images/sol.svg',
  TRUMP: 'https://sdk-cdn.fun.xyz/images/trump.png',
  UBTC: 'https://sdk-cdn.fun.xyz/images/btc.svg',
  USDe: 'https://sdk-cdn.fun.xyz/images/usde.svg',
  USDT: 'https://sdk-cdn.fun.xyz/images/usdt.svg',
  WBNB: 'https://sdk-cdn.fun.xyz/images/bsc.svg',
  WETH: 'https://sdk-cdn.fun.xyz/images/weth.svg',
};

// 官网支持的token如下，接口返回的不一致，暂时以官网的为准
export const SUPPORTED_TOKEN_SYMBOLS = [
  'USDC',
  'USDC.e', // USD Coin
  'ARB',
  'BNB',
  'BTC',
  'BTCB',
  'BUSD',
  'CBBTC',
  'DAI',
  'ETH',
  'HYPE',
  'MATIC',
  'MON',
  'POL',
  'SOL',
  'TRUMP',
  'UBTC',
  'USDe',
  'USDT',
  'WBNB',
  'WETH',
];

/** Predict Bridge 聚合后的单条链信息（用于下拉等） */
export interface PredictBridgeChainItem {
  chainId: string;
  chainName: string;
  minCheckoutUsd: number;
  logo: string;
}

/** 从 supported-assets 派生：链列表、白名单 token、token→支持的网络 id 列表，及与平台链的映射结果 */
export interface ConvertAssetsResult {
  pdChains: PredictBridgeChainItem[];
  /** Bridge 资产中出现过的 token symbol，去重后按字母序 */
  pdTokenSymbols: string[];
  /**
   * key: token symbol 小写，value: 该 token 支持的 chainId 数组，顺序与 pdChains 一致。
   * 切换币种时可直接取 value[0] 作为默认网络。
   */
  tokenChainIds: Record<string, string[]>;
  /**
   * key: chainId，value: 该网络支持的 token 对象列表（按 address 去重，顺序稳定）。
   */
  chainTokens: Record<string, SupportedAssetToken[]>;
  /** Bridge 返回的 chainName 集合与平台 chainList 按名称交集后的可选链，顺序同 chainList */
  predictConvertChains: ChainInfo[];
}

function normalizeSupportedAsset(item: SupportedAsset): SupportedAsset {
  const isUsdceCoin = item?.token?.name === 'USD Coin (Arb1)' || item?.token?.name === 'USD Coin (PoS)';
  return {
    ...item,
    chainId: Number(item.chainId) === POLYMARKET_BRIDGE_SOLANA_NUMERIC_ID ? String(SOLANA_CHAIN_ID) : String(item.chainId),
    token: {
      ...item.token,
      symbol: isUsdceCoin ? 'USDC.e' : item.token.symbol,
    },
  };
}

function logoForPdChainId(chainId: string): string {
  const key = Number(chainId) as keyof typeof pdChainLogos;
  return pdChainLogos[key]?.logo ?? '';
}

/**
 * 处理 Polymarket Bridge supportedAssets：token 展示映射、链/token 聚合；预测划转可选链由 Bridge chainName 与平台 chainList（chainName / chainNameForWallet）名称交集得到。
 * 纯函数；平台链表使用 `@/app/const/chain` 的 chainList。
 */
export function convertPdAssets(pdAssets: SupportedAsset[]): ConvertAssetsResult {
  const normalized = pdAssets.map(normalizeSupportedAsset);

  const chainMap = new Map<string, PredictBridgeChainItem>();
  const tokenToChainIdSet = new Map<string, Set<string>>();
  const chainIdToTokenMap = new Map<string, Map<string, SupportedAssetToken>>();
  const tokenSymbolSet = new Set<string>();
  const bridgeChainNameSet = new Set<string>();

  for (const a of normalized) {
    const { chainId } = a;
    const symLower = a.token.symbol.toLowerCase();

    tokenSymbolSet.add(a.token.symbol);
    bridgeChainNameSet.add(a.chainName);

    if (!chainMap.has(chainId)) {
      chainMap.set(chainId, {
        chainId,
        chainName: a.chainName === 'BNB Smart Chain' ? 'BSC' : a.chainName,
        minCheckoutUsd: a.minCheckoutUsd ?? 0,
        logo: logoForPdChainId(chainId),
      });
    }

    if (!tokenToChainIdSet.has(symLower)) tokenToChainIdSet.set(symLower, new Set());
    tokenToChainIdSet.get(symLower)!.add(chainId);

    if (!chainIdToTokenMap.has(chainId)) chainIdToTokenMap.set(chainId, new Map());
    chainIdToTokenMap.get(chainId)!.set(a.token.address.toLowerCase(), a.token);
  }

  const pdChains = [...chainMap.values()];
  const pdTokenSymbols = [...tokenSymbolSet].sort((a, b) => a.localeCompare(b));

  const tokenChainIds: Record<string, string[]> = {};
  for (const [symLower, chainIdSet] of tokenToChainIdSet) {
    tokenChainIds[symLower] = pdChains.map(c => c.chainId).filter(id => chainIdSet.has(id));
  }

  const chainTokens: Record<string, SupportedAssetToken[]> = {};
  for (const chain of pdChains) {
    const list = [...(chainIdToTokenMap.get(chain.chainId)?.values() ?? [])];
    list.sort((a, b) => (a.symbol || '').localeCompare(b.symbol || '') || (a.address || '').localeCompare(b.address || ''));
    chainTokens[chain.chainId] = list;
  }

  // 预测和dex有交集的网络
  const predictConvertChains = chainList.filter(c => bridgeChainNameSet.has(c.chainName) || bridgeChainNameSet.has(c.chainNameForWallet));

  console.log(111, {
    pdChains,
    pdTokenSymbols,
    tokenChainIds,
    chainTokens,
    predictConvertChains,
  });

  return {
    pdChains,
    pdTokenSymbols,
    tokenChainIds,
    chainTokens,
    predictConvertChains,
  };
}
